import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import Papa from "papaparse";

interface MedicineRow {
  name: string;
  type: string;
  manufacturer: string;
  brand?: string;
  description?: string;
  batchNo?: string;
  mrpPrice?: number;
  buyingPrice?: number;
  quantity?: number;
  strip_count?: number;
  strip_cost?: number;
  medicines_per_strip?: number;
  expiryDate?: string; // dd-mm-yyyy or YYYY-MM-DD
}

const BulkUploadMedicine: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const catalogMode = location.pathname.startsWith("/services")
    ? { label: "Service", plural: "Services", category: "SERVICE", basePath: "/services", template: "service_template.csv" }
    : location.pathname.startsWith("/labs")
      ? { label: "Lab", plural: "Labs", category: "LAB", basePath: "/labs", template: "lab_template.csv" }
      : { label: "Medicine", plural: "Medicines", category: "MEDICINE", basePath: "/medicines", template: "medicine_template.csv" };
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<MedicineRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    redirectTo: undefined as string | undefined,
  });

const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];

  if (!file) {
    return;
  }

  setCsvFile(file);
  handleParseCSV(file);
};

  // --------------------------
  // Parse CSV for preview
  // --------------------------
  const isMedicineMode = catalogMode.category === "MEDICINE";
  const calculateStripStock = (stripCount?: number | string, medicinesPerStrip?: number | string) => {
    const strips = Number(stripCount || 0);
    const perStrip = Number(medicinesPerStrip || 0);
    return strips > 0 && perStrip > 0 ? strips * perStrip : 0;
  };
  const calculateUnitCost = (stripCost?: number | string, medicinesPerStrip?: number | string) => {
    const cost = Number(stripCost || 0);
    const perStrip = Number(medicinesPerStrip || 0);
    return cost > 0 && perStrip > 0 ? Number((cost / perStrip).toFixed(2)) : 0;
  };

  const handleParseCSV = (csvFile?: File) => {
    if (!csvFile) return;

      Papa.parse(csvFile, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          transformHeader: (h) => h.trim(),
          complete: (results) => {
              const data: MedicineRow[] = (results.data as MedicineRow[]).map((row) => ({
                  ...row,
                  name: row.name?.trim(),
                  manufacturer: row.manufacturer?.trim() || (!isMedicineMode ? "Clinic" : ""),
                  brand: row.brand?.trim() || (!isMedicineMode ? catalogMode.label : row.brand),
                  type: catalogMode.category,
                  strip_count: isMedicineMode ? Number(row.strip_count || 0) : undefined,
                  strip_cost: isMedicineMode ? Number(row.strip_cost || 0) : undefined,
                  medicines_per_strip: isMedicineMode ? Number(row.medicines_per_strip || 0) : undefined,
                  mrpPrice: isMedicineMode ? calculateUnitCost(row.strip_cost, row.medicines_per_strip) : Number(row.mrpPrice || 0),
                  buyingPrice: 0,
                  quantity: isMedicineMode ? calculateStripStock(row.strip_count, row.medicines_per_strip) : Number(row.quantity || 0),
              }));
              setPreviewData(data);
          },
          error: (err) => {
                console.error("CSV Parse Error:", err);
                  setModal({
                      open: true,
                      title: "CSV Parse Error",
                      message: err.message,
                      redirectTo: undefined,
                  });
                  return;

          },
      });
  };

  // --------------------------
  // Submit bulk upload
  // --------------------------
  const handleUpload = async () => {
    if (!previewData.length) return;

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/medicines/bulk`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(previewData),
      });

      if (response.ok) {
        setModal({
          open: true,
          title: "Success",
          message: `${catalogMode.plural} bulk upload completed successfully`,
          redirectTo: catalogMode.basePath,
        });
      } else {
        const err = await response.json();
        setModal({
          open: true,
          title: "Upload Failed",
          message: err.message || "Failed to upload",
          redirectTo: undefined,
        });
      }
    } catch (error: any) {
      console.error(error);
      setModal({
        open: true,
        title: "Error",
        message: "Something went wrong",
        redirectTo: undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  // --------------------------
  // Download CSV template
  // --------------------------
  const downloadTemplate = () => {
    const headers = !isMedicineMode
      ? ["name", "manufacturer", "description", "mrpPrice"]
      : [
        "name",
        "type",
        "manufacturer",
        "brand",
        "description",
        "batchNo",
        "strip_count",
        "strip_cost",
        "medicines_per_strip",
        "expiryDate",
      ];
    const csvContent = [headers.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", catalogMode.template);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <SidebarLayout title={`Bulk Upload ${catalogMode.plural}`}>
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const r = modal.redirectTo;
          setModal({ ...modal, open: false });
          if (r) navigate(r);
        }}
      />

      <div className="bg-surface-light p-6 rounded border max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upload CSV</h2>
          <button
            onClick={downloadTemplate}
            className="px-4 py-2 bg-primary text-white rounded"
          >
            Download Template
          </button>
        </div>

        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="mb-4"
        />
        {previewData.length > 0 && (
          <div className="overflow-x-auto border rounded p-2 mb-4">
            <table className="w-full table-auto">
              <thead className="bg-gray-100">
                <tr>
                  {Object.keys(previewData[0]).map((header) => (
                    <th key={header} className="p-2 text-left text-sm font-medium">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, idx) => (
                  <tr key={idx} className="border-t">
                    {Object.values(row).map((val, i) => (
                      <td key={i} className="p-2 text-sm">
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
          onClick={() => navigate(catalogMode.basePath)}
            className="px-4 py-2 rounded bg-background-light"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleUpload}
            //disabled={!previewData.length || loading}
            className="px-6 py-2 rounded bg-primary text-white"
          >
            {loading ? "Uploading..." : "Upload All"}
          </button>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default BulkUploadMedicine;
